import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { useGetVerificationHistory } from '../../../services/loanService';
import { VerificationHistory, VerificationHistoryStatus } from '../../../services/schemas/loanServiceSchemas';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const Verification = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const ALL_VERIFICATIONS_PAGE_SIZE = 10000;

    // Fetch verification history
    const {
        data: verificationHistoryPage,
        isLoading,
        error
    } = useGetVerificationHistory(
        { page: 0, size: ALL_VERIFICATIONS_PAGE_SIZE }
    );

    // Handle errors with useEffect
    useEffect(() => {
        if (error) {
            toast.error(`Failed to load verification history: ${(error as Error).message}`);
        }
    }, [error]);

    // Map status to badge color
    const getStatusBadgeColor = (status?: VerificationHistoryStatus) => {
        switch (status) {
            case 'VERIFICATION_COMPLETE':
                return 'success';
            case 'IN_PROGRESS':
                return 'warning';
            case 'VERIFICATION_REJECTED':
                return 'danger';
            case 'PENDING':
            default:
                return 'info';
        }
    };

    // Format status for display
    const formatStatus = (status?: VerificationHistoryStatus) => {
        switch (status) {
            case 'VERIFICATION_COMPLETE':
                return 'Completed';
            case 'IN_PROGRESS':
                return 'In Progress';
            case 'VERIFICATION_REJECTED':
                return 'Rejected';
            case 'PENDING':
                return 'Pending';
            default:
                return 'Unknown';
        }
    };

    // Format date for display
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
        } catch (error) {
            return dateString;
        }
    };

    const columns = [
        {
            name: 'Application ID',
            selector: (row: VerificationHistory) => row.applicationNumber || 'N/A',
            sortable: true,
        },
        {
            name: 'Customer Name',
            selector: (row: VerificationHistory) => row.customerName || 'N/A',
            sortable: true,
        },
        {
            name: 'Business Name',
            selector: (row: VerificationHistory) => row.customerBusinessName || 'N/A',
            sortable: true,
        },
        {
            name: 'Application Type',
            selector: (row: VerificationHistory) => row.type || 'N/A',
            sortable: true,
        },
        {
            name: 'Status',
            selector: (row: VerificationHistory) => formatStatus(row.status),
            cell: (row: VerificationHistory) => (
                <span className={`badge bg-${getStatusBadgeColor(row.status)}`}>
                    {formatStatus(row.status)}
                </span>
            ),
        },
        {
            name: 'Created Date',
            selector: (row: VerificationHistory) => formatDate(row.createdDate),
            sortable: true,
        },
        {
            name: 'Verified By',
            selector: (row: VerificationHistory) => row.verifiedBy || 'N/A',
            sortable: true,
        },
    ];

    // Calculate verification stats
    const verificationStats = [
        {
            title: 'Total Verifications',
            value: (verificationHistoryPage?.content?.length || 0).toString(),
            icon: 'fa-list-check',
            color: 'primary'
        },
        {
            title: 'Pending',
            value: verificationHistoryPage?.content?.filter(item => item.status === 'PENDING').length.toString() || '0',
            icon: 'fa-clock',
            color: 'warning'
        },
        {
            title: 'Completed',
            value: verificationHistoryPage?.content?.filter(item => item.status === 'VERIFICATION_COMPLETE').length.toString() || '0',
            icon: 'fa-check-circle',
            color: 'success'
        },
        {
            title: 'Rejected',
            value: verificationHistoryPage?.content?.filter(item => item.status === 'VERIFICATION_REJECTED').length.toString() || '0',
            icon: 'fa-times-circle',
            color: 'danger'
        },
    ];

    // Handle page change
    const handlePageChange = (page: number) => {
        setPage(page);
    };

    // Handle rows per page change
    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setSize(newPerPage);
        setPage(page);
    };

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Verification History</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Verification Stats */}
                    <div className="row g-3 mb-3 row-deck">
                        {verificationStats.map((stat, index) => (
                            <div key={index} className="col-lg-3 col-md-6 col-sm-6">
                                <div className="card">
                                    <div className="card-body">
                                        <i className={`fa ${stat.icon} position-absolute top-0 end-0 mt-4 me-3 text-${stat.color}`} style={{ fontSize: '24px' }}></i>
                                        <div className="mb-2 text-uppercase">{stat.title}</div>
                                        <div><span className="h4">{stat.value}</span></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Verification History */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">Verification History</h6>
                                </div>
                                <div className="card-body">
                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-3">Loading verification history...</p>
                                        </div>
                                    ) : error ? (
                                        <div className="alert alert-danger">
                                            <h5 className="alert-heading">Error Loading Data</h5>
                                            <p>{(error as Error).message || 'An unknown error occurred'}</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={verificationHistoryPage?.content || []}
                                                pagination
                                                onChangePage={handlePageChange}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                paginationPerPage={size}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                highlightOnHover
                                                striped
                                                responsive
                                                progressPending={isLoading}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Verification;
